import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchIncomeVerificationApi } from "../../../../../api/client";
import { mapCallScriptHistory } from "./callScript.mapping";
import type { CallScriptHistory } from "./callScript.types";
import { callScriptKeys } from "./callScriptKeys";

/**
 * Shared queryOptions for an applicant's saved Call Script history (key +
 * queryFn). Backed by the generated
 * WorkbenchIncomeVerificationApi.getHistory1, whose `object`
 * response is mapped to entries by `mapCallScriptHistory`.
 *
 * Disabled until both ids are present and the caller opts in. Two call
 * sites: the Call Script modal (only while it's open) and each Income Info
 * block's Verification Outcome label (spec §8, always enabled once ids are
 * present) — the shared query key means both dedupe to a single request per
 * applicant regardless of how many income blocks render for that person.
 * @param {string} approvalId - The deal / approval identifier (path param).
 * @param {string} applicantId - The applicant identifier (path param).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guards.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function callScriptHistoryQuery(approvalId: string, applicantId: string, enabled = true) {
  return queryOptions({
    queryKey: callScriptKeys.history({ approvalId, applicantId }),
    queryFn: async ({ signal }): Promise<CallScriptHistory> => {
      const response = await workbenchIncomeVerificationApi.getHistory1(approvalId, applicantId, {
        signal,
      });
      return mapCallScriptHistory(response.data);
    },
    staleTime: 30_000,
    retry: false,
    enabled: enabled && !!approvalId && !!applicantId,
  });
}

/**
 * Reads an applicant's saved Call Script history. Disabled until the approval
 * id and applicant id are present and the caller enables it — the Call
 * Script modal enables it only while open; Verification Outcome labels (spec
 * §8) enable it whenever both ids are present, independent of the modal.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} applicantId - The applicant identifier.
 * @param {{ enabled?: boolean }} [options] - Caller opt-in (defaults to enabled).
 * @param {boolean} [options.enabled] - When false, the query never fires.
 * @returns {ReturnType<typeof useQuery<CallScriptHistory>>} The TanStack Query result.
 */
export function useCallScriptHistory(approvalId: string, applicantId: string, options?: { enabled?: boolean }) {
  return useQuery(callScriptHistoryQuery(approvalId, applicantId, options?.enabled ?? true));
}
